import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Eye, EyeOff, ArrowUp } from 'lucide-react';

interface ContentProps {
  content: string;
  showContent: boolean;
  onToggleVisibility: () => void;
  onCopy: () => void;
}

export default function Content({
  content,
  showContent,
  onToggleVisibility,
  onCopy
}: ContentProps) {
  const { t } = useTranslation('viewNote');
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show button when user scrolls down 500px
      setShowBackToTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <div className="flex justify-end space-x-2 mb-4">
          <button
            onClick={onCopy}
            className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            title={t('content.actions.copy')}
          >
            <Copy className="w-5 h-5 mr-2" />
            {t('content.actions.copy')}
          </button>
          <button
            onClick={onToggleVisibility}
            className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            title={showContent ? t('content.actions.hide') : t('content.actions.show')}
          >
            {showContent ? (
              <>
                <EyeOff className="w-5 h-5 mr-2" />
                {t('content.actions.hide')}
              </>
            ) : (
              <>
                <Eye className="w-5 h-5 mr-2" />
                {t('content.actions.show')}
              </>
            )}
          </button>
        </div>
        <div 
          className={`
            relative bg-gray-50 p-6 rounded-xl border border-gray-200
            transition-all duration-300 ease-in-out
            ${showContent ? 'opacity-100' : 'opacity-90'}
          `}
        >
          <div 
            className="whitespace-pre-wrap font-mono text-base text-gray-800"
            dangerouslySetInnerHTML={{ __html: content }}
          />
          {!showContent && (
            <div 
              className="absolute inset-0 backdrop-blur-[6px] bg-gray-50/50 rounded-xl"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_2px,#f3f4f6_2px,#f3f4f6_4px)]" />
            </div>
          )}
        </div>
      </div>

      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 p-4 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 transition-all duration-200 animate-fade-in"
          aria-label={t('content.actions.backToTop')}
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}